import { Navigate, useSearchParams } from "react-router-dom";
import { LoadingSpinner } from "./ui/loading-spinner";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";

interface ResponderProtectedRouteProps {
    children: JSX.Element;
}

const ResponderProtectedRoute = ({ children }: ResponderProtectedRouteProps) => {
    const { loading } = useAuth();
    const [isResponderAuthenticated, setIsResponderAuthenticated] = useState(false);
    const [responderLoading, setResponderLoading] = useState(true);
    const [searchParams] = useSearchParams();
    const assignmentId = searchParams.get("id");

    // Check authentication status
    const checkAuth = () => {
        const storedToken = localStorage.getItem("responder_auth_token");
        setIsResponderAuthenticated(!!storedToken);
        setResponderLoading(false);
    };

    useEffect(() => {
        // Initial check
        checkAuth();

        // Listen for storage changes (when token is removed in another tab)
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === "responder_auth_token") {
                checkAuth();
            }
        };

        // Listen for custom event (for same-tab changes)
        const handleTokenRemoved = () => {
            checkAuth();
        };

        globalThis.addEventListener("storage", handleStorageChange);
        globalThis.addEventListener("responder-token-removed", handleTokenRemoved);

        // Periodic check every 2 seconds as fallback
        const interval = setInterval(() => {
            const currentToken = localStorage.getItem("responder_auth_token");
            if (!currentToken && isResponderAuthenticated) {
                setIsResponderAuthenticated(false);
            }
        }, 2000);

        return () => {
            globalThis.removeEventListener("storage", handleStorageChange);
            globalThis.removeEventListener("responder-token-removed", handleTokenRemoved);
            clearInterval(interval);
        };
    }, [isResponderAuthenticated]);

    // Validate assignmentId
    if (!assignmentId) {
        return <Navigate to="/error?id=400" replace />;
    }

    if (responderLoading || loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <LoadingSpinner size="large" color="rgb(5 83 156)" />
            </div>
        );
    }

    if (!isResponderAuthenticated) {
        // Redirect to login page with assignmentId
        return <Navigate to={`/login?id=${assignmentId}`} replace />;
    }

    return children;
};

export default ResponderProtectedRoute;
